import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Package, Users, LayoutDashboard, Navigation, Building2, DollarSign, FileText, BarChart3, MessageSquare, LogOut, Download, Sparkles, Inbox, Zap, Gift, FolderOpen, Crown, Command, Bot, TrendingUp, Wallet, PiggyBank, Fuel, Phone, Menu, ChevronDown, X, Rocket, Shield, ShieldCheck, Mail, Kanban, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatInterface, ChatButton } from '@/components/ChatInterface';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';
import TrialBanner from '@/components/TrialBanner';
import { toast } from 'sonner';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/0ee8c733-9ebe-4e41-9af6-e5aa20e6c9c1/images/664a6582c52f759cf8aa9c469d5205aa85a496bab31400c484f1532a982ea379.png";

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Dispatcher ID - in production this would come from auth
  const dispatcherId = 'dispatcher-001';
  const dispatcherName = 'Dispatch Center';

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
    }
    setDeferredPrompt(null);
    setShowInstallBtn(false);
  };

  // Grouped navigation items
  const navGroups = {
    main: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
      { icon: Rocket, label: 'Autopilot', path: '/admin/autopilot' },
      { icon: DollarSign, label: 'Revenue', path: '/admin/revenue' },
      { icon: Command, label: 'Command', path: '/admin/command' },
    ],
    operations: {
      label: 'Operations',
      icon: Package,
      items: [
        { icon: Truck, label: 'Loads', path: '/admin/loads' },
        { icon: Users, label: 'Fleet', path: '/admin/fleet' },
        { icon: Building2, label: 'Brokers', path: '/admin/brokers' },
        { icon: Navigation, label: 'Dispatch', path: '/admin/dispatch' },
        { icon: Inbox, label: 'Pipeline', path: '/admin/pipeline' },
        { icon: ShieldCheck, label: 'Carrier Compliance', path: '/admin/carrier-compliance' },
        { icon: Rocket, label: 'Setup Status', path: '/admin/setup-status' },
        { icon: Sparkles, label: 'Enterprise Proposal', path: '/admin/enterprise/proposal' },
        { icon: Kanban, label: 'Proposal Pipeline', path: '/admin/proposals' },
        { icon: Mail, label: 'Email Campaigns', path: '/admin/campaigns' },
      ]
    },
    ai: {
      label: 'AI Tools',
      icon: Bot,
      items: [
        { icon: Bot, label: 'AI Dispatcher', path: '/ai-dispatcher' },
        { icon: Bot, label: 'Automation', path: '/admin/automation' },
        { icon: Zap, label: 'Negotiator', path: '/admin/negotiator' },
        { icon: Sparkles, label: 'AI Assistant', path: '/admin/email-assistant' },
        { icon: TrendingUp, label: 'Rate Predictor', path: '/rate-predictor' },
        { icon: BarChart3, label: 'Lane Intelligence', path: '/admin/lane-intelligence' },
        { icon: Shield, label: 'Driver Trust', path: '/admin/driver-trust' },
        { icon: Phone, label: 'Voice Agent', path: '/voice-agent' },
      ]
    },
    finance: {
      label: 'Finance',
      icon: DollarSign,
      items: [
        { icon: Wallet, label: 'Quick Pay', path: '/quickpay' },
        { icon: PiggyBank, label: 'Factoring', path: '/factoring' },
        { icon: Fuel, label: 'Fuel Card', path: '/fuel-card' },
        { icon: DollarSign, label: 'Invoices', path: '/admin/invoices' },
      ]
    },
    more: {
      label: 'More',
      icon: Menu,
      items: [
        { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
        { icon: FolderOpen, label: 'Documents', path: '/admin/documents' },
        { icon: MessageSquare, label: 'Messaging', path: '/admin/messaging' },
        { icon: Gift, label: 'Referrals', path: '/admin/referrals' },
        { icon: FileText, label: 'Contracts', path: '/admin/contracts' },
        { icon: Crown, label: 'Premium', path: '/admin/premium' },
      ]
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminLoggedIn');
    navigate('/admin');
  };

  const toggleDropdown = (key) => {
    setActiveDropdown(activeDropdown === key ? null : key);
  };

  const isGroupActive = (items) => {
    return items.some(item => location.pathname === item.path);
  };

  const DropdownMenu = ({ groupKey, group }) => {
    const Icon = group.icon;
    const isActive = isGroupActive(group.items);
    const isOpen = activeDropdown === groupKey;
    const menuRef = React.useRef(null);

    return (
      <div className="relative" ref={menuRef}>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdown(groupKey);
          }}
          variant={isActive ? 'default' : 'ghost'}
          size="sm"
          className={`gap-2 ${
            isActive
              ? 'bg-primary text-white shadow-[0_0_20px_rgba(59,130,246,0.3)]'
              : 'hover:bg-accent'
          }`}
        >
          <Icon className="w-4 h-4" />
          <span className="hidden lg:inline">{group.label}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>
        
        {isOpen && (
          <div className="absolute top-full left-0 mt-2 w-48 bg-background/95 backdrop-blur-lg border border-border rounded-lg shadow-xl z-50 py-2">
            {group.items.map((item) => {
              const ItemIcon = item.icon;
              const isItemActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveDropdown(null);
                    navigate(item.path);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors cursor-pointer ${
                    isItemActive
                      ? 'bg-primary/20 text-primary'
                      : 'hover:bg-accent text-foreground'
                  }`}
                >
                  <ItemIcon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="layout-container">
      <TrialBanner />
      <nav className="glass border-b border-border/50 sticky top-0 z-50" data-testid="navigation-bar">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Link to="/admin/dashboard">
                <img 
                  src={LOGO_URL} 
                  alt="Tempus Dispatch LLC" 
                  className="h-16 lg:h-20"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1 lg:gap-2">
              {/* Main nav items */}
              {navGroups.main.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    className={`gap-2 ${
                      isActive
                        ? 'bg-primary text-white shadow-[0_0_20px_rgba(59,130,246,0.3)]'
                        : 'hover:bg-accent'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden lg:inline">{item.label}</span>
                  </Button>
                );
              })}

              {/* Dropdown menus */}
              <DropdownMenu groupKey="operations" group={navGroups.operations} />
              <DropdownMenu groupKey="ai" group={navGroups.ai} />
              <DropdownMenu groupKey="finance" group={navGroups.finance} />
              <DropdownMenu groupKey="more" group={navGroups.more} />
              
              {/* Actions */}
              <div className="ml-2 pl-2 border-l border-border flex items-center gap-2">
                <ChatButton userId={dispatcherId} onClick={() => setChatOpen(true)} />
                
                {showInstallBtn && (
                  <Button
                    onClick={handleInstall}
                    variant="outline"
                    size="sm"
                    className="gap-2 border-green-500 text-green-400 hover:bg-green-500/10"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden lg:inline">Install</span>
                  </Button>
                )}
                
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden lg:inline">Logout</span>
                </Button>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <ChatButton userId={dispatcherId} onClick={() => setChatOpen(true)} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-border pt-4">
              <div className="grid grid-cols-2 gap-2">
                {navGroups.main.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      variant={isActive ? 'default' : 'ghost'}
                      size="sm"
                      className="justify-start gap-2"
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </Button>
                  );
                })}
                {Object.entries(navGroups).filter(([key]) => key !== 'main').map(([key, group]) => (
                  group.items && group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setMobileMenuOpen(false);
                        }}
                        variant={isActive ? 'default' : 'ghost'}
                        size="sm"
                        className="justify-start gap-2"
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </Button>
                    );
                  })
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-border flex gap-2">
                {showInstallBtn && (
                  <Button
                    onClick={handleInstall}
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2 border-green-500 text-green-400"
                  >
                    <Download className="w-4 h-4" />
                    Install App
                  </Button>
                )}
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="sm"
                  className="flex-1 gap-2 text-red-400"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-screen-2xl mx-auto" data-testid="main-content">
        {children}
      </main>

      <PlatformDisclaimer />

      {/* Chat Interface */}
      <ChatInterface
        userId={dispatcherId}
        userName={dispatcherName}
        userType="dispatcher"
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
      />
    </div>
  );
};

export default Layout;
